import { PermissionsAndroid, Platform } from 'react-native';

type Geolocation = typeof import('@react-native-community/geolocation').default;
type GeoOptions = { enableHighAccuracy: boolean; maximumAge: number; timeout: number };
type GeoFailure = { code: number; message?: string };

export type CommentLocation = {
  latitude: number;
  longitude: number;
  locationCapturedAt: string;
};

/**
 * denied: permission refused, may be asked again.
 * blocked: permission refused permanently; only device Settings can change it.
 * disabled: device location services are off.
 * unavailable: services and permission are fine but no fix arrived in time.
 */
export type CommentLocationFailure = 'denied' | 'blocked' | 'disabled' | 'unavailable';

export class CommentLocationError extends Error {
  constructor(readonly reason: CommentLocationFailure, message: string) {
    super(message);
    this.name = 'CommentLocationError';
  }
}

const PERMISSION_CODE = 1;
// Not a native error code: the platform returned a fix with non-finite values.
const INVALID_FIX_CODE = -1;
// AndroidLocationManager reports this when no enabled provider matches the granted permission.
const ANDROID_NO_PROVIDER = 'No location provider available.';
const FIX_TIMEOUT_MS = 15000;

function defaultMessage(reason: CommentLocationFailure): string {
  switch (reason) {
    case 'denied':
      return 'Location permission is required to add a comment. Allow location access and try again.';
    case 'blocked':
      return Platform.OS === 'ios'
        ? 'Location access is turned off for this app. Open Settings, choose Location, select "While Using the App", and try again.'
        : 'Location permission was permanently denied. Open Settings, allow Location for this app, and try again.';
    case 'disabled':
      return 'Location services are turned off. Turn on location services and try again.';
    case 'unavailable':
      return 'Your current location is temporarily unavailable. Move to an open area or check your network, then try again.';
  }
}

function fail(reason: CommentLocationFailure, message = defaultMessage(reason)) {
  return new CommentLocationError(reason, message);
}

function loadGeolocation(): Geolocation {
  // Load the native module only when location is requested.
  return require('@react-native-community/geolocation').default;
}

function requestPosition(
  geolocation: Geolocation,
  options: GeoOptions,
): Promise<CommentLocation> {
  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        if (
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude) ||
          !Number.isFinite(position.timestamp)
        ) {
          reject({ code: INVALID_FIX_CODE } satisfies GeoFailure);
          return;
        }
        resolve({
          latitude,
          longitude,
          locationCapturedAt: new Date(position.timestamp).toISOString(),
        });
      },
      error => reject({ code: error.code, message: error.message } satisfies GeoFailure),
      options,
    );
  });
}

/** Resolves with the first successful fix; rejects with every failure if none succeed. */
function firstFix(attempts: Promise<CommentLocation>[]): Promise<CommentLocation> {
  return new Promise((resolve, reject) => {
    const failures: GeoFailure[] = [];
    attempts.forEach(attempt =>
      attempt.then(resolve, (failure: GeoFailure) => {
        failures.push(failure);
        if (failures.length === attempts.length) reject(failures);
      }),
    );
  });
}

async function ensureAndroidPermission(): Promise<{ precise: boolean }> {
  const { ACCESS_FINE_LOCATION, ACCESS_COARSE_LOCATION } = PermissionsAndroid.PERMISSIONS;
  const { GRANTED, NEVER_ASK_AGAIN } = PermissionsAndroid.RESULTS;
  // Returns immediately without a prompt when access is already granted.
  const results = await PermissionsAndroid.requestMultiple([
    ACCESS_FINE_LOCATION,
    ACCESS_COARSE_LOCATION,
  ]);
  const precise = results[ACCESS_FINE_LOCATION] === GRANTED;
  if (precise || results[ACCESS_COARSE_LOCATION] === GRANTED) return { precise };
  if (
    results[ACCESS_FINE_LOCATION] === NEVER_ASK_AGAIN ||
    results[ACCESS_COARSE_LOCATION] === NEVER_ASK_AGAIN
  ) {
    throw fail('blocked');
  }
  throw fail('denied');
}

async function captureAndroidLocation(): Promise<CommentLocation> {
  const { precise } = await ensureAndroidPermission();
  const geolocation = loadGeolocation();
  // enableHighAccuracy alone pins Android to the GPS provider, which rarely gets a
  // fresh fix indoors. Ask the network provider (Wi-Fi/cell) in parallel and keep
  // whichever fresh fix arrives first. GPS also needs precise (fine) permission.
  const attempts = [
    requestPosition(geolocation, {
      enableHighAccuracy: false,
      maximumAge: 0,
      timeout: FIX_TIMEOUT_MS,
    }),
  ];
  if (precise) {
    attempts.push(
      requestPosition(geolocation, {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: FIX_TIMEOUT_MS,
      }),
    );
  }
  try {
    return await firstFix(attempts);
  } catch (failures) {
    const errors = failures as GeoFailure[];
    if (errors.some(error => error.code === PERMISSION_CODE)) throw fail('denied');
    if (errors.every(error => error.message === ANDROID_NO_PROVIDER)) {
      throw fail(
        'disabled',
        precise
          ? undefined
          : 'Location services are turned off, or this app only has approximate location. Turn on location services or allow precise location, then try again.',
      );
    }
    throw fail('unavailable');
  }
}

async function captureIosLocation(): Promise<CommentLocation> {
  const geolocation = loadGeolocation();
  geolocation.setRNConfiguration({
    skipPermissionRequests: false,
    authorizationLevel: 'whenInUse',
    enableBackgroundLocationUpdates: false,
  });
  let lastFailure: GeoFailure | undefined;
  // iOS reports kCLErrorLocationUnknown as a hard failure even though it is transient,
  // so retry once with a coarser accuracy before giving up.
  for (const enableHighAccuracy of [true, false]) {
    try {
      return await requestPosition(geolocation, {
        enableHighAccuracy,
        maximumAge: 0,
        timeout: FIX_TIMEOUT_MS,
      });
    } catch (failure) {
      lastFailure = failure as GeoFailure;
      // On iOS a denial (or Location Services switched off) can only be undone in Settings.
      if (lastFailure.code === PERMISSION_CODE) throw fail('blocked');
      if (lastFailure.message === 'Location services disabled.') throw fail('disabled');
    }
  }
  throw fail('unavailable');
}

async function captureWebLocation(): Promise<CommentLocation> {
  const geolocation = loadGeolocation();
  if (
    !(globalThis as { navigator?: { geolocation?: unknown } }).navigator
      ?.geolocation
  ) {
    throw new Error('Location is unavailable in this browser.');
  }
  try {
    return await requestPosition(geolocation, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 20000,
    });
  } catch (failure) {
    const code = (failure as GeoFailure).code;
    if (code === INVALID_FIX_CODE) {
      throw new Error('Your current location is unavailable. Please try again.');
    }
    throw new Error(
      code === PERMISSION_CODE
        ? 'Location permission is required to add a comment. Allow location access in Settings and try again.'
        : 'Unable to get your current location. Turn on location services and try again.',
    );
  }
}

/** A fresh fix is required for every comment; never reuse punch coordinates. */
export function captureCommentLocation(): Promise<CommentLocation> {
  if (Platform.OS === 'android') return captureAndroidLocation();
  if (Platform.OS === 'ios') return captureIosLocation();
  return captureWebLocation();
}
